import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const WA_NUMBER = '918651427150'; // ← your WhatsApp number


const SUGGESTED_QUESTIONS = [
  "How does renting work?",
  "What's the security deposit?",
  "Can you deliver to my hostel?",
  "How do I return an outfit?",
  "What payment methods are accepted?",
  "How do I track my order?",
];

export default function WarbadobeChatbot() {
  const { user } = useAuth();
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hey${user?.name ? ` ${user.name.split(' ')[0]}` : ''}! 👋 I'm Warba, your Warbadobe assistant. Ask me anything about renting, delivery, returns, or payments — I'm here to help!`,
      ts: Date.now(),
    }
  ]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [unread, setUnread]   = useState(0);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText || loading) return;
    setInput('');
    setEscalated(false);

    const newMessages = [...messages, { role: 'user', content: userText, ts: Date.now() }];
    setMessages(newMessages);
    setLoading(true);

    try {
      // Build conversation history for API (exclude timestamps)
      const history = newMessages.map(m => ({ role: m.role, content: m.content }));

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(API_BASE + '/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          messages: history,
          userName: user?.name || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Server error ${response.status}`);
      }

      const displayText   = data.reply || "Sorry, I couldn't process that. Please try again.";
      const shouldEscalate = data.escalate || false;
      const escalateReason = data.escalateReason || '';

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: displayText,
        ts: Date.now(),
        escalate: shouldEscalate,
        escalateReason,
      }]);

      if (shouldEscalate) setEscalated(true);
      if (!open) setUnread(u => u + 1);

    } catch (err) {
      const errMsg = err.message?.includes('fetch')
        ? "Can't reach the server. Make sure the backend is running on port 5000."
        : "I'm having trouble right now. Please try again or reach out on WhatsApp.";
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errMsg,
        ts: Date.now(),
        escalate: true,
        escalateReason: 'Connection error',
      }]);
      setEscalated(true);
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = (reason) => {
    const msg = reason
      ? `Hi! I need help with: ${reason}`
      : `Hi! I need help with my Warbadobe order.`;
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const fmt = (ts) => new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      {/* ── Chat Window ── */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 88, right: 16, zIndex: 1000,
          width: 'min(360px, calc(100vw - 32px))',
          height: 'min(520px, calc(100vh - 120px))',
          background: 'white',
          border: '1px solid var(--border)',
          boxShadow: '0 12px 48px rgba(0,0,0,0.16)',
          display: 'flex', flexDirection: 'column',
          animation: 'chatSlideUp 0.25s ease',
        }}>
          <style>{`
            @keyframes chatSlideUp {
              from { opacity: 0; transform: translateY(16px) scale(0.97); }
              to   { opacity: 1; transform: translateY(0)   scale(1);    }
            }
            .chat-msg-user { animation: msgIn 0.18s ease; }
            .chat-msg-bot  { animation: msgIn 0.18s ease; }
            @keyframes msgIn {
              from { opacity: 0; transform: translateY(6px); }
              to   { opacity: 1; transform: translateY(0);   }
            }
            .chat-input:focus { outline: none; border-color: var(--gold) !important; }
            .chat-send:hover  { background: #333 !important; }
            .chat-pill:hover  { background: var(--ink) !important; color: var(--cream) !important; }
            .wa-btn:hover     { background: #1ebe5d !important; }
          `}</style>

          {/* Header */}
          <div style={{ background: 'var(--ink)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '2px solid var(--gold)', flexShrink: 0 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 16 }}>
              W
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'white', fontWeight: 700, fontSize: 13, fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '0.06em' }}>Warba</div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10 }}>
                {loading ? 'typing...' : 'Warbadobe Assistant · Online'}
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: 20, lineHeight: 1, padding: 4, display: 'flex', alignItems: 'center' }}>×</button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 6px', display: 'flex', flexDirection: 'column', gap: 10 }}>

            {messages.map((msg, i) => (
              <div key={i} className={msg.role === 'user' ? 'chat-msg-user' : 'chat-msg-bot'}
                style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 3 }}>

                <div style={{
                  maxWidth: '82%',
                  padding: '9px 13px',
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: msg.role === 'user' ? 'white' : 'var(--ink)',
                  background: msg.role === 'user' ? 'var(--ink)' : 'var(--cream)',
                  border: msg.role === 'user' ? 'none' : '1px solid var(--border)',
                  borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {msg.content}
                </div>

                {/* Escalate button */}
                {msg.escalate && (
                  <button className="wa-btn" onClick={() => openWhatsApp(msg.escalateReason)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#25D366', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: 'white', letterSpacing: '0.06em', marginTop: 2, borderRadius: 2, transition: 'background 0.15s' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Chat on WhatsApp
                  </button>
                )}

                <span style={{ fontSize: 9, color: '#bbb', letterSpacing: '0.04em' }}>{fmt(msg.ts)}</span>
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                <div style={{ padding: '10px 14px', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '12px 12px 12px 2px', display: 'flex', gap: 4, alignItems: 'center' }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--muted)', animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                  <style>{`@keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-5px)} }`}</style>
                </div>
              </div>
            )}

            {/* Suggested questions — show only at start */}
            {messages.length === 1 && !loading && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button key={i} className="chat-pill" onClick={() => sendMessage(q)}
                    style={{ padding: '6px 12px', fontSize: 11, background: 'white', border: '1px solid var(--border)', color: 'var(--ink)', cursor: 'pointer', transition: 'all 0.15s', borderRadius: 2, fontFamily: "'Josefin Sans', sans-serif" }}>
                    {q}
                  </button>
                ))}
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexShrink: 0, background: 'white' }}>
            <input
              ref={inputRef}
              className="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Ask me anything..."
              style={{ flex: 1, padding: '9px 12px', border: '1px solid var(--border)', fontSize: 13, fontFamily: "'Josefin Sans', sans-serif", background: 'var(--cream)', transition: 'border-color 0.15s', outline: 'none' }}
              disabled={loading}
            />
            <button className="chat-send" onClick={() => sendMessage()} disabled={loading || !input.trim()}
              style={{ width: 38, height: 38, background: input.trim() ? 'var(--ink)' : 'var(--cream-dark)', border: 'none', cursor: input.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={input.trim() ? 'var(--gold)' : 'var(--muted)'} strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>

          {/* Footer */}
          <div style={{ padding: '8px 12px 10px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <button onClick={() => openWhatsApp()} style={{ fontSize: 10, fontWeight: 700, color: '#25D366', background: 'none', border: '1px solid #25D36633', padding: '6px 16px', cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Talk to us on WhatsApp
            </button>
          </div>
        </div>
      )}

      {/* ── Floating Button ── */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', bottom: 16, right: 16, zIndex: 1000,
          width: 52, height: 52, borderRadius: '50%',
          background: open ? '#333' : 'var(--ink)',
          border: '2px solid var(--gold)',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        title="Chat with Warba"
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        )}

        {/* Unread badge */}
        {unread > 0 && !open && (
          <div style={{ position: 'absolute', top: -2, right: -2, width: 18, height: 18, borderRadius: '50%', background: '#ef5350', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: 'white' }}>
            {unread}
          </div>
        )}
      </button>
    </>
  );
}